import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {EC_FILE_EXPORT_CONDO_NEW_AUC_SEU, EC_FILE_EXPORT_CONDO_OLD_AUC_SEU, PlantIncentiveMode} from 'smag-commons/cfg/hierarchy';
import {
    ECCacvRedistributionMode,
    ECEconomicsPlantsResponse,
    ECPlantTip,
    ECPlantTipMode,
    ECPlantTipType,
    STRATEGY_NEW_CONDO,
    STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco';
import {I18NService} from '../../../../i18n/i18n.service';
import {DialogService} from '../../../../shared/dialog/dialog.service';
import {ValueObjectType} from 'smag-commons/cfg/value';
import {SmagApiService} from '../../../../api/smag-api.service';
import {MsgBoxService} from '../../../../shared/msg-box.service';
import {PlantsPod} from '../ec-plants.component';
import {downloadBlob, isDefined} from '../../../../commons/commons';
import {Router} from '@angular/router';
import {HierarchyEntity} from '../../../../shared/hierarchy.service';
import {CONTENT_DIALOG, DATE_SELECTION} from '../../economics-aggregated/ec-select-date/ec-select-date.component';
import {ReiResponse} from '../set-rei/set-rei.component';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE, REDISTRIBUTION_SETTINGS} from 'smag-commons/cfg/economics-values';
import {TipResponse} from '../set-tip/set-tip.component';
import {CACV_STRING, isUpdatable, missingCosts, mkYearlyValue, YearSummaryConsolidation} from '../../../../shared/ec-economics';
import {NiceValuePipe} from '../../../../shared/shared.pipe';
import {formatEuroValue, formatPercentValue} from '../../../../shared/ec-members';
import {EconomicsConfig} from 'smag-commons/cfg';
import {mkTruncatedMemberName} from '../../../../shared/energy-community';
import {ALGORITHM_CONDO_NEW, ALGORITHM_CONDO_OLD} from 'smag-commons/dto/eco/community';


@Component({
    selector: 'app-ec-plants-details',
    templateUrl: './ec-plants-details.component.html',
    styleUrls: ['./ec-plants-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ECPlantsDetailsComponent implements OnInit {
    private readonly nvp: NiceValuePipe;

    @Input() economicsPlantsResponse: ECEconomicsPlantsResponse;
    @Input() plantsPod: PlantsPod;
    @Input() entity: HierarchyEntity;
    @Input() date: Moment;
    @Input() timeZone: string;
    @Input() summaryConsolidations: YearSummaryConsolidation[];
    @Input() minDate: Moment;
    @Input() maxDate: Moment;
    @Input() isWholeYear: boolean;
    @Input() enableTipConfig: boolean;
    @Input() strategy: string;
    @Input() ecId: string;
    @Input() fromTime: number;
    @Input() toTime: number;
    @Input() isSingleMonth: boolean;
    @Input() selectedYear: number;
    @Input() isRepresentative: boolean;
    @Input() generatesTip: boolean;
    @Input() economicsConfig: EconomicsConfig;
    @Output() detailsDataEvent: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('configureTipQuote') private configureTipQuoteDialog: TemplateRef<any>;
    @ViewChild('configureRei') private configureReiDialog: TemplateRef<any>;
    @ViewChild('selectDate') private selectDateDialog: TemplateRef<any>;
    @ViewChild('setBilledData') private setBilledDataDialog: TemplateRef<any>;

    currentDateSelection: DATE_SELECTION = DATE_SELECTION.MONTH;
    currentContent: CONTENT_DIALOG = CONTENT_DIALOG.MONTHLY;
    capitalGrantUse = false;
    templateIsDownloading = false;
    templateIsDisabled = true;

    readonly isDefined = isDefined;
    readonly missingCosts = missingCosts;
    readonly mkTruncatedMemberName = mkTruncatedMemberName;
    readonly CACV_STRING = CACV_STRING;
    readonly PlantIncentiveMode = PlantIncentiveMode;

    constructor(private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly router: Router) {
        this.nvp = new NiceValuePipe(i18n);
    }

    ngOnInit() {
        this.capitalGrantUse = this.plantsPod.plants.some(plant => plant.capital_grant > 0);
        this.apiService.getYearlyValue(mkYearlyValue(REDISTRIBUTION_SETTINGS, null, this.entity.id, this.selectedYear))
            .subscribe(yv => this.templateIsDisabled = yv.value.algorithm !== ALGORITHM_CONDO_OLD && yv.value.algorithm !== ALGORITHM_CONDO_NEW);
    }

    get isNotRepresentative() {
        return !this.isRepresentative || this.isRepresentative && this.plantsPod.communityPlant;
    }

    get nonIncentivizedPlants() {
        return this.plantsPod.plants.length > 0 &&
            this.plantsPod.plants.some(plant => isDefined(plant.incentive_mode) &&
                plant.incentive_mode === PlantIncentiveMode.NO_INCENTIVE);
    }

    get plantTip() {
        // easy workaround for new condo
        if (this.isCondomNew) {
            return this.nvp.transform(0, '€');
        }

        if (!isDefined(this.plantsPod.tip_configs)) {
            return '-';
        }

        let tip: string;
        let tipQuote: string;
        if (this.plantsPod.tip_configs.type === ECPlantTipType.GROSS) {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.gross_tip || 0, '€', 2, this.plantsPod.gross_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentive_shared_energy / 1000 || 0, 'MWh', 5, this.plantsPod.incentive_shared_energy < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        } else if (this.plantsPod.tip_configs.type === ECPlantTipType.NET) {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.net_tip || 0, '€', 2, this.plantsPod.net_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentive_shared_energy / 1000 || 0, 'MWh', 5, this.plantsPod.incentive_shared_energy < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        } else {
            if (this.plantsPod.tip_configs.mode === ECPlantTipMode.PERCENTAGE) {
                tip = this.nvp.transform(this.plantsPod.non_surplus_tip || 0, '€', 2, this.plantsPod.non_surplus_tip < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0) * 100, '%', 2, this.plantsPod.tip_configs.plant_tip < 0);
            } else {
                tip = this.nvp.transform(this.plantsPod.incentivized_shared_energy_non_surplus / 1000 || 0, 'MWh', 5, this.plantsPod.incentivized_shared_energy_non_surplus < 0);
                tipQuote = this.nvp.transform((this.plantsPod.tip_configs.plant_tip || 0), '€/MWh', 2, this.plantsPod.tip_configs.plant_tip < 0);
            }
        }

        const plantTip = this.nvp.transform(this.plantsPod.plant_tip || 0, '€', 2, this.plantsPod.plant_tip < 0);
        return tip + ' * ' + tipQuote + ' = ' + plantTip;
    }

    get plantCacv() {
        if (!this.plantsPod || !isDefined(this.plantsPod.plant_c_acv)) {
            return '-';
        }
        if (this.isCondomOld || this.isCondomNew) {
            return formatEuroValue(this.plantsPod.plant_c_acv, this.nvp);
        }
        switch (this.plantsPod.cacvMode) {
            case ECCacvRedistributionMode.REDISTRIBUTION:
            case ECCacvRedistributionMode.PLANTS:
                return formatEuroValue(this.plantsPod.plant_c_acv, this.nvp);
            case ECCacvRedistributionMode.AS_TIP:
                return this.plantCacvAsTip();
            default:
                return this.nvp.transform(0, '€');
        }
    }

    get utilitiesAbsorptionFee() {
        const netTip = this.plantsPod.net_tip || 0;
        const grossTip = this.plantsPod.gross_tip || 0;
        const plantTip = this.plantsPod.plant_tip || 0;
        let tip: number;
        if (this.isCondomOld) {
            tip = grossTip - plantTip;
        } else if (this.isCondomNew) {
            tip = grossTip; // nothing goes to plants
        } else {
            tip = netTip - plantTip;
        }
        return this.i18n.translate('Utilities absorption fee') + ': ' + this.nvp.transform(tip, '€', 2, tip < 0);
    }

    get netTip() {
        return this.isDefined(this.plantsPod.net_tip) ?
            this.nvp.transform(this.plantsPod.net_tip, '€', 2, this.plantsPod.net_tip < 0) :
            '- €';
    }

    get grossTip() {
        return this.isDefined(this.plantsPod.gross_tip) ?
            this.nvp.transform(this.plantsPod.gross_tip, '€', 2, this.plantsPod.gross_tip < 0) :
            '- €';
    }

    get canUpdate() {
        return isUpdatable(this.entity);
    }

    get isCondomNew() {
        return this.strategy === STRATEGY_NEW_CONDO;
    }

    get isCondomOld() {
        return this.strategy === STRATEGY_OLD_CONDO;
    }

    openConfigureDialog() {
        if (this.isWholeYear) {
            if (!this.plantsPod.communityPlant) {
                this.openTipDialog();
            } else {
                this.openReiDialog();
            }
        } else {
            this.currentContent = CONTENT_DIALOG.YEARLY;
            this.currentDateSelection = DATE_SELECTION.YEAR;
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select an annual period to configure'),
                template: this.selectDateDialog,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.selectedYear = data.year();
                    if (!this.plantsPod.communityPlant) {
                        this.openTipDialog();
                    } else {
                        this.openReiDialog();
                    }
                }
            });
        }
    }

    onClickGoToPlant(id: string) {
        this.router.navigate(['/c', 'ec', 'resources'],
            {
                queryParams: {id},
                queryParamsHandling: 'merge'
            }).then();
    }

    hasFutureActivation(date: number) {
        return isDefined(date) ? date > moment.tz(this.timeZone).valueOf() : false;
    }

    onClickInsertBilledData() {
        if (this.isSingleMonth) {
            this.openBilledDialog();
        } else {
            this.currentContent = CONTENT_DIALOG.MONTHLY;
            this.currentDateSelection = DATE_SELECTION.MONTH;
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select a month to insert billed data'),
                template: this.selectDateDialog,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.date = data;
                    this.openBilledDialog();
                }
            });
        }
    }

    onClickGoToMembers() {
        this.router.navigate(['/c', 'ec', 'members'],
            {
                queryParams: {id: this.entity.id},
                queryParamsHandling: 'merge'
            }).then();
    }

    isExportSupported(): boolean {
        return (this.isCondomOld || this.isCondomNew) &&
            this.isWholeYear &&
            !!this.plantsPod.seu;
    }

    exportEconomicsToExcel() {
        this.templateIsDownloading = true;
        switch (this.strategy) {
            case STRATEGY_OLD_CONDO:
            case STRATEGY_NEW_CONDO:
                this.apiService.exportEconomicsToFile(
                    this.isCondomNew ? EC_FILE_EXPORT_CONDO_NEW_AUC_SEU : EC_FILE_EXPORT_CONDO_OLD_AUC_SEU,
                    this.ecId,
                    this.fromTime,
                    this.toTime,
                    this.plantsPod.pod)
                    .subscribe(([data, filename]) => {
                        downloadBlob(data, filename);
                        this.templateIsDownloading = false;
                        this.msgBoxService.info(this.i18n.translate('File downloaded!'));
                    }, error => {
                        this.templateIsDownloading = false;
                        this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'));
                    });
                break;
            default:
                break;

        }

    }

    private openTipDialog() {
        this.dialogService.open<any, TipResponse>({
            title: this.i18n.translate('Configure the premium fee designated to "{0}"\'s plant', this.plantsPod.entity.name),
            template: this.configureTipQuoteDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            if (data) {
                const value: YearlyValue = {
                    name: PLANT_TIP_QUOTE,
                    object_id: this.plantsPod.pod,
                    object_type: ValueObjectType.EC_POD,
                    value: {
                        type: data.type,
                        mode: data.mode,
                        plant_tip: data.plant_tip
                    } as ECPlantTip,
                    year: data.year
                };
                this.apiService.setYearlyValue(value).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Plant configured correctly'));
                        this.detailsDataEvent.emit();
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring plant'), err));
            }
        });
    }

    private openReiDialog() {
        this.dialogService.open<any, ReiResponse>({
            title: this.i18n.translate('Configure the net revenue of the "{0}"\'s plants', this.plantsPod.entity.name),
            template: this.configureReiDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            if (data) {
                const value: YearlyValue = {
                    name: EC_PLANTS_REI_REDISTRIBUTION,
                    object_id: this.plantsPod.pod,
                    object_type: ValueObjectType.EC_POD,
                    value: data.useReiVal ? 1 : 0,
                    year: data.year
                };
                this.apiService.setYearlyValue(value).then(() => {
                        this.msgBoxService.info(this.i18n.translate('Plant configured correctly'));
                        this.detailsDataEvent.emit();
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring plant'), err));
            }
        });
    }

    private openBilledDialog() {
        this.dialogService.open<any, any>({
            title: this.i18n.translate('Enter data billed on energy withdrawal input the plants of \"{0}\"', this.plantsPod.entity.name),
            template: this.setBilledDataDialog,
            context: {
                name: this.entity.name
            }
        }).closed.then(data => {
            // TODO send api req to insert billed data when BE is ready
        });
    }

    private plantCacvAsTip(): string {
        if (!isDefined(this.plantsPod.tip_configs)) {
            return '-';
        }
        const tipMode = this.plantsPod.tip_configs.mode;
        const tipType = this.plantsPod.tip_configs.type;
        const plantCacv = formatEuroValue(this.plantsPod.plant_c_acv ?? 0, this.nvp);
        if (tipMode === ECPlantTipMode.PERCENTAGE) {
            const plantTip = formatPercentValue(this.plantsPod.tip_configs.plant_tip ?? 0, this.nvp);
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                cacvValue = this.plantsPod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET || tipType === ECPlantTipType.NON_SURPLUS) {
                cacvValue = this.plantsPod.net_c_acv;
            }
            const cacv = formatEuroValue(cacvValue ?? 0, this.nvp);
            return `${cacv} * ${plantTip} = ${plantCacv}`;
        } else if (tipMode === ECPlantTipMode.EURO_MGW) {
            const plantTip = formatEuroValue(this.plantsPod.plant_tip ?? 0, this.nvp);
            let tipValue = 0;
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                tipValue = this.plantsPod.gross_tip;
                cacvValue = this.plantsPod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET) {
                tipValue = this.plantsPod.net_tip;
                cacvValue = this.plantsPod.net_c_acv;
            } else if (tipType === ECPlantTipType.NON_SURPLUS) {
                tipValue = this.plantsPod.non_surplus_tip;
                cacvValue = this.plantsPod.net_c_acv;
            }
            const tip = formatEuroValue(tipValue ?? 0, this.nvp);
            const cacv = formatEuroValue(cacvValue ?? 0, this.nvp);
            return `(${plantTip} / ${tip}) * ${cacv} = ${plantCacv}`;
        }
    }

}
